import { useState } from 'react';
import { api } from '../lib/api';
import type { Device } from '../lib/api';
import {
  Empty,
  ErrorNotice,
  Loading,
  Notice,
  OnlineBadge,
  Panel,
  relativeTime,
  useAsync,
} from '../components/ui';

interface PublishResult {
  messageId: string;
  targetCount: number;
  onlineCount: number;
  offlineCount: number;
  skipped: number;
  message: string;
}

interface StoredMessage {
  id: string;
  title: string;
  body: string;
  target_count: number;
  created_at: string;
  created_by_email: string | null;
}

export default function NotificationsPage() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<PublishResult | null>(null);

  const devices = useAsync((signal) => api.get<{ devices: Device[] }>('/devices', signal), []);
  const list = useAsync(
    (signal) => api.get<{ messages: StoredMessage[] }>('/notifications', signal),
    [],
    15_000,
  );

  const enrolled = (devices.data?.devices ?? []).filter((d) => d.enrollmentStatus === 'enrolled');

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    setSelected(selected.size === enrolled.length ? new Set() : new Set(enrolled.map((d) => d.id)));
  };

  const publish = async () => {
    if (!title.trim() || !body.trim() || selected.size === 0) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const created = await api.post<PublishResult>('/notifications', {
        title: title.trim(),
        body: body.trim(),
        deviceIds: [...selected],
      });
      setResult(created);
      setTitle('');
      setBody('');
      setSelected(new Set());
      list.reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h2>Notifications</h2>
          <p>
            Publish a message to selected tablets. It appears as a full-screen notice on the
            device at the next check-in — including during kiosk — and does not change policy.
          </p>
        </div>
      </div>

      <ErrorNotice error={error} />

      {result && (
        <Notice kind="info">
          <strong>{result.message}</strong>
        </Notice>
      )}

      <Panel title="Publish a message">
        <label>
          <span>Title</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={120}
            placeholder="e.g. Exam starts in 10 minutes"
          />
        </label>
        <label>
          <span>Message</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={2000}
            rows={4}
            placeholder="Shown on the tablet. Students tap OK to dismiss it."
          />
        </label>

        <div className="actions" style={{ marginBottom: 8 }}>
          <strong>Devices</strong>
          <button className="small" type="button" onClick={selectAll}>
            {selected.size === enrolled.length && enrolled.length > 0 ? 'Clear all' : 'Select all'}
          </button>
          <span className="muted small">{selected.size} of {enrolled.length} selected</span>
        </div>

        {devices.loading && !devices.data ? (
          <Loading what="devices" />
        ) : enrolled.length === 0 ? (
          <Empty>No enrolled devices to notify.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th style={{ width: 32 }} /><th>Device</th><th>Student</th><th>Status</th><th>Last seen</th></tr>
              </thead>
              <tbody>
                {enrolled.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selected.has(d.id)}
                        onChange={() => toggle(d.id)}
                        aria-label={`Select ${d.displayName}`}
                      />
                    </td>
                    <td>
                      {d.displayName}
                      <div className="small muted">{d.model}</div>
                    </td>
                    <td>{d.student?.studentName ?? <span className="muted">Unassigned</span>}</td>
                    <td><OnlineBadge state={d.online} /></td>
                    <td className="small muted">{relativeTime(d.lastSeenAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="actions" style={{ marginTop: 12 }}>
          <button
            className="primary"
            type="button"
            onClick={publish}
            disabled={busy || !title.trim() || !body.trim() || selected.size === 0}
          >
            {busy ? 'Publishing…' : `Publish to ${selected.size} device(s)`}
          </button>
        </div>
        <p className="small muted" style={{ marginBottom: 0 }}>
          There is no instant push. Online tablets usually show the message within about 20 seconds.
        </p>
      </Panel>

      <Panel title="Recently published">
        {list.loading && !list.data ? (
          <Loading what="messages" />
        ) : (list.data?.messages.length ?? 0) === 0 ? (
          <Empty>No messages have been published yet.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Title</th><th>Message</th><th className="num">Devices</th>
                  <th>When</th><th>By</th>
                </tr>
              </thead>
              <tbody>
                {list.data!.messages.map((m) => (
                  <tr key={m.id}>
                    <td>{m.title}</td>
                    <td className="small">{m.body}</td>
                    <td className="num">{m.target_count}</td>
                    <td className="small muted">{relativeTime(m.created_at)}</td>
                    <td className="small muted">{m.created_by_email ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}
